import DropdownTheme from "css/themes/Dropdown.css";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import withStyles from "react-jss";
import Select, { components } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import Card from "react-toolbox/lib/card";
import DatePicker from "react-toolbox/lib/date_picker";
import Dropdown from "react-toolbox/lib/dropdown";
import Input from "react-toolbox/lib/input";
import COLOURS from "util/colours";
import { H2, H3 } from "visual-components/util/texts";
import Pagination from "./pagination";

const styles = {
  container: {
    padding: 40,
  },
  heading: {
    marginBottom: 24,
  },
  filterSection: {
    marginBottom: 24,
    padding: 16,
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
  },
  filterItem: {
    flex: "1 1 250px",
    minWidth: 220,
    display: "flex",
    flexDirection: "column",
    marginLeft: 12,
  },
  filterLabel: {
    marginBottom: 6,
    fontWeight: 500,
    fontSize: 14,
    color: "#333",
  },
  filterButton: {
    marginLeft: 16,
    backgroundColor: "transparent",
    border: "1px solid #007FFF",
    color: "#007FFF",
    padding: "6px 12px",
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#007FFF",
      color: "#fff",
    },
  },
  loader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    "& .spinner": {
      width: 24,
      height: 24,
      border: "4px solid #007FFF",
      borderTop: "4px solid transparent",
      borderRadius: "50%",
      animation: "$spin 1s linear infinite",
    },
  },
  tableWrapper: {
    marginTop: 16,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },
  tableHeader: {
    backgroundColor: COLOURS.silver,
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    borderBottom: "2px solid #ddd",
    fontWeight: 600,
    backgroundColor: "#f5f7fa",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #eee",
  },
  tableRow: {
    "&:nth-child(even)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#eaf4ff",
      transition: "0.2s",
    },
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

// Custom Checkbox Option for campaign select
const Option = (props) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        style={{ marginRight: 8 }}
      />
      <label>{props.label}</label>
    </components.Option>
  );
};

// Months array
const currentYear = moment().year();
const months = moment.months().map((m, idx) => ({
  value: idx,
  label: `${m} ${currentYear}`,
}));

function CSAdminEntriesManager(props) {
  const { classes } = props;

  const [validationError, setValidationError] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [disableExport, setDisableExport] = useState(false);
  const [total, setTotal] = useState(0);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 When month changes, update fromDate & toDate
  const handleMonthChange = (option) => {
    setSelectedMonth(option.value);
    const start = moment().month(option.value).startOf("month").format("YYYY-MM-DD");
    const end = moment().month(option.value).endOf("month").format("YYYY-MM-DD");
    setFromDate(start);
    setToDate(end);
    setCampaigns([]);
    setSelectedCampaigns([]);
    setEntries([]);
    setPage(1);
    setTotal(0);
    setDisableExport(false);
    setValidationError("");
  };

  // 🔹 Fetch campaign options
  const loadOptions = useCallback(
    async (search, loadedOptions, { page }) => {
      try {
        const response = await fetch(
          `/cs-campaign-lookup?search=${encodeURIComponent(search)}&fromDate=${formatDate(fromDate)}&toDate=${formatDate(toDate)}`,
        );
        const result = await response.json();
        const options = result.data.map((campaign) => ({
          value: campaign.id,
          label: campaign.name,
        }));
        setCampaigns(options);
        return {
          options: [{ value: "select_all", label: "✅ Select All" }, ...options],
          hasMore: false,
          additional: { page: 1 },
        };
      } catch (error) {
        console.error("Error fetching campaigns", error);
        return {
          options: [],
          hasMore: false,
          additional: { page: 1 },
        };
      }
    },
    [fromDate, toDate], // 👈 will re-create when month changes
  );

  // 🔹 Select All / Clear All logic
  const handleCampaignChange = (selected) => {
    if (selected && selected.some((opt) => opt.value === "select_all")) {
      if (selectedCampaigns.length === campaigns.length) {
        setSelectedCampaigns([]);
      } else {
        setSelectedCampaigns(campaigns);
      }
    } else {
      setSelectedCampaigns(selected || []);
    }
    setDisableExport(false);
  };

  const formatDate = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY-MM-DD");
  };

  const handleFilter = async (page = 1) => {
    if (!selectedCampaigns.length) {
      toastr.error("Please select Campaign(s).", null, {
        timeOut: 2000,
        positionClass: "toast-bottom-center",
      });
      return;
    }
    setValidationError("");
    setEntries([]);
    const BATCH_SIZE = 10;
    const batches = [];
    let overallTotal = 0;
    try {
      const campaignIds = selectedCampaigns.map((c) => c.value);
      for (let i = 0; i < campaignIds.length; i += BATCH_SIZE) {
        batches.push(campaignIds.slice(i, i + BATCH_SIZE));
      }
      for (const batch of batches) {
        setLoading(true);
        const params = new URLSearchParams();
        params.append("campaignIds", batch.join(","));
        if (fromDate) params.append("fromDate", formatDate(fromDate));
        if (toDate) params.append("toDate", formatDate(toDate));
        const res = await fetch(`/cs-entries-lookup?${params.toString()}`);
        let result = await res.json();
        let rows = result.entries;
        let batchSum = rows.reduce((acc, el) => acc + Number(el.total_entries || 0), 0);

        overallTotal += batchSum;
        if (rows.length) {
          setEntries((prev) => {
            const merged = [...prev, ...rows];
            return merged.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          });
        }
        setLoading(false);
      }
      setTotal(overallTotal);
    } catch (err) {
      console.error("Failed to fetch entries", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      if (!selectedCampaigns.length || !fromDate || !toDate) {
        toastr.error("Please select Campaign(s).", null, {
          timeOut: 2000,
          positionClass: "toast-bottom-center",
        });
        return;
      }
      setDisableExport(true);
      const params = new URLSearchParams();
      params.append("campaignIds", selectedCampaigns.map((c) => c.value).join(","));
      if (fromDate) params.append("fromDate", formatDate(fromDate));
      if (toDate) params.append("toDate", formatDate(toDate));
      const res = await fetch(`/cs-entries-export?${params.toString()}`);
      const result = await res.json();
      if (result.OK) {
        toastr.success("Entries list has been sent to hello@dojomojo.com.", null, {
          timeOut: 5000,
          positionClass: "toast-bottom-center",
        });
      }
    } catch (error) {
      console.error("Error exporting CSV", error);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <H2 azure>CS Admin - Entries Manager</H2>
      </div>

      {/* Filter Section */}
      <div className={classes.filterSection}>
        <H3>Filter Entries</H3>
        {validationError && <span style={{ color: "red", marginTop: 8 }}>{validationError}</span>}
        <div className={classes.filterRow}>
          {/* 🔹 Month Dropdown */}
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Select Month</label>
            <Select
              value={months.find((m) => m.value === selectedMonth)}
              onChange={handleMonthChange}
              options={months}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          {/* 🔹 Campaign Dropdown */}
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Select Campaign(s)</label>
            <AsyncPaginate
              key={`${fromDate}-${toDate}`} // 🔑 Force remount when dates change
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option }}
              additional={{ page: 1 }}
              value={selectedCampaigns}
              onChange={handleCampaignChange}
              loadOptions={(search, loadedOptions, additional) =>
                loadOptions(search, loadedOptions, additional, fromDate, toDate)
              }
              placeholder="Select Campaign(s)"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => handleFilter(1)}
            className={classes.filterButton}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Processing..." : "Show details"}
          </button>
          <button
            onClick={() => handleExportCSV()}
            className={classes.filterButton}
            style={{
              opacity: disableExport ? 0.6 : 1,
              cursor: disableExport ? "not-allowed" : "pointer",
            }}
            disabled={disableExport}
          >
            Export CSV
          </button>
        </div>
      </div>

      {entries.length > 0 ? (
        <div style={{ marginTop: 32 }}>
          <H3>Results:</H3>
          <div className={classes.filterRow} style={{ marginTop: 15 }}>
            <div className={classes.filterItem} style={{ marginTop: 15 }}>
              Total Campaign(s): {campaigns.length}
            </div>
            <div className={classes.filterItem} style={{ marginTop: 15 }}>
              Selected Campaign(s): {selectedCampaigns.length}
            </div>
            <div className={classes.filterItem} style={{ marginTop: 15 }}>
              Total Entries: {loading ? "Calculating..." : total}
            </div>
          </div>
          <table className={classes.table} style={{ marginTop: 32 }}>
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.th}>Campaign Name</th>
                <th className={classes.th}>Start date</th>
                <th className={classes.th}>End date</th>
                <th className={classes.th}>Total entries</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: "16px", textAlign: "center", color: "#999" }}>
                    No entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className={classes.tableRow}>
                    <td className={classes.td}>{entry.name}</td>
                    <td className={classes.td}>{moment(entry.start_date).format("YYYY-MM-DD")}</td>
                    <td className={classes.td}>{moment(entry.end_date).format("YYYY-MM-DD")}</td>
                    <td className={classes.td}>{entry.total_entries}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

CSAdminEntriesManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CSAdminEntriesManager);
